import { useCallback, useEffect, useState } from "react";
import { IconCheck } from "../icons";
import logoBranca from "../assets/logo-branca.png";

/**
 * A PÁGINA DE QUEM PEDIU — /pedido/<protocolo>.
 *
 * Antes, quem abria uma solicitação de dentro do RH (ou de qualquer sistema)
 * recebia "registrada" e sumia no escuro: a única forma de saber do andamento
 * era perguntar por WhatsApp — exatamente o que este sistema veio evitar. Aqui
 * a pessoa vê em que etapa o pedido está, desde quando, e a resposta quando ela
 * existe.
 *
 * O que ela NÃO vê: a fila do desenvolvedor, os comentários internos, o contato
 * de quem pediu ou qualquer outro pedido. O protocolo é aleatório justamente
 * para que trocar o número na barra de endereço não abra o pedido dos outros.
 */

interface Pedido {
  protocolo: string;
  titulo: string;
  descricao: string | null;
  sistema: string | null;
  tipo: string;
  status: string;
  criado_em: string;
  status_em: string | null;
  prazo: string | null;
  resposta: string | null;
  respondido_em: string | null;
  entregue_em: string | null;
  entrega: string | null;
}

/** As etapas na ordem — a linha do tempo desenhada na tela. */
const ETAPAS = ["Recebida", "Em análise", "Em desenvolvimento", "Aguardando retorno", "Concluída"];

/** O que cada etapa quer dizer para quem está esperando (e não para o dev). */
const EXPLICACAO: Record<string, string> = {
  Recebida: "Seu pedido chegou e está na fila para ser analisado.",
  "Em análise": "O desenvolvedor está avaliando o que precisa ser feito.",
  "Em desenvolvimento": "A mudança está sendo feita no sistema.",
  "Aguardando retorno": "Falta uma informação sua ou uma confirmação para continuar.",
  Concluída: "Pronto! A mudança foi entregue.",
  Recusada: "Este pedido não vai seguir. O motivo está na resposta abaixo.",
};

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR", { dateStyle: "long" });
}

export default function PedidoPublico({ protocolo }: { protocolo: string }) {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await fetch("/api/pedido/" + encodeURIComponent(protocolo));
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErro(body?.error ?? "Não foi possível consultar este protocolo.");
        return;
      }
      setPedido(body);
      setErro(null);
    } catch {
      setErro("Não foi possível consultar agora. Tente de novo em instantes.");
    }
  }, [protocolo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const etapaAtual = pedido ? ETAPAS.indexOf(pedido.status) : -1;
  const recusada = pedido?.status === "Recusada";

  return (
    <div className="pub">
      <header className="pub-topo">
        <img src={logoBranca} alt="SERCAMP" />
        <span>SERCAMP · Desenvolvimento de Software</span>
      </header>

      <main className="pub-corpo">
        <div className="pub-card">
          {!pedido ? (
            <p>{erro ?? "Consultando…"}</p>
          ) : (
            <>
              <span className="pub-protocolo">Protocolo {pedido.protocolo}</span>
              <h1>{pedido.titulo}</h1>
              <p className="muted">
                Aberto em {quando(pedido.criado_em)}
                {pedido.sistema ? " · sistema: " + pedido.sistema : ""} · {pedido.tipo}
              </p>

              <div className={"pub-situacao" + (recusada ? " recusada" : "")}>
                <strong>{pedido.status}</strong>
                <span>{EXPLICACAO[pedido.status] ?? ""}</span>
                {pedido.status_em && (
                  <small>nesta etapa desde {quando(pedido.status_em)}</small>
                )}
              </div>

              {/* A LINHA DO TEMPO só aparece no caminho normal: num pedido
                  recusado ela mostraria etapas que nunca vão acontecer. */}
              {!recusada && (
                <ol className="pub-etapas">
                  {ETAPAS.map((etapa, i) => (
                    <li
                      key={etapa}
                      className={i < etapaAtual ? "feita" : i === etapaAtual ? "atual" : ""}
                    >
                      <span className="pub-bolinha">{i < etapaAtual ? <IconCheck size={12} /> : i + 1}</span>
                      {etapa}
                    </li>
                  ))}
                </ol>
              )}

              {pedido.descricao && (
                <>
                  <h2>O que você pediu</h2>
                  <p className="pub-texto">{pedido.descricao}</p>
                </>
              )}

              {pedido.resposta && (
                <>
                  <h2>Resposta do desenvolvedor</h2>
                  <p className="pub-texto">{pedido.resposta}</p>
                  {pedido.respondido_em && (
                    <small className="muted">respondido em {quando(pedido.respondido_em)}</small>
                  )}
                </>
              )}

              {pedido.entrega && (
                <p className="pub-entrega">
                  <IconCheck size={14} /> Entregue em {quando(pedido.entregue_em)}: {pedido.entrega}
                </p>
              )}

              <p className="muted pub-rodape-nota">
                Esta página se atualiza sozinha a cada consulta — guarde o endereço para acompanhar.
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="pub-rodape">
        SERCAMP Serviços · Completa em Manutenção e Prevenção Elétrica
      </footer>
    </div>
  );
}
